import React, { useEffect, useState } from 'react';
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Link,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import {
  formatApiError,
  requestPasswordReset,
  confirmPasswordReset,
  getAuthProviders,
  AuthProviders,
} from '../services/api';

const ResetPassword: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { signIn } = useAuth();
  const token = searchParams.get('token');

  // Whether the server can actually email a reset link. Until it loads we don't
  // render the request form, so we never briefly promise an email we can't send.
  const [providers, setProviders] = useState<AuthProviders | null>(null);
  useEffect(() => {
    getAuthProviders()
      .then(setProviders)
      .catch(() => setProviders({ github: false, google: false, email_reset: false }));
  }, []);

  // --- request state (no token) ---
  const [identifier, setIdentifier] = useState('');
  const [requested, setRequested] = useState(false);

  // --- confirm state (has token) ---
  const [newPassword, setNewPassword] = useState('');
  const [confirmPw, setConfirmPw] = useState('');

  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const onRequest = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await requestPasswordReset(identifier.trim());
      setRequested(true);
    } catch (err: any) {
      setError(formatApiError(err, 'Something went wrong. Please try again.'));
    } finally {
      setSubmitting(false);
    }
  };

  const onConfirm = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    if (newPassword !== confirmPw) {
      setError('The two passwords don’t match.');
      return;
    }
    setSubmitting(true);
    try {
      const { access_token } = await confirmPasswordReset(token as string, newPassword);
      await signIn(access_token);
      navigate('/teacher/library');
    } catch (err: any) {
      setError(formatApiError(err, 'This reset link is invalid or has expired. Request a new one.'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 440, mx: 'auto', pt: 6 }}>
      <Card>
        <CardContent sx={{ p: 4 }}>
          {!token ? (
            providers === null ? (
              <Typography variant="body2" color="text.secondary">Loading…</Typography>
            ) : providers.email_reset === false ? (
              <>
                <Typography variant="h4" component="h1" sx={{ mb: 1 }}>
                  Password reset isn’t available yet
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  This server can’t send reset emails right now, so we can’t reset a
                  password by email yet.
                </Typography>
                <Alert severity="info" sx={{ mb: 3 }}>
                  {providers.github
                    ? 'Signed up with GitHub? Use “Sign in with GitHub” on the sign-in page — GitHub accounts don’t use a password, so nothing to reset.'
                    : 'If you signed up with GitHub, use “Sign in with GitHub”. Otherwise, contact your administrator to restore access.'}
                </Alert>
                <Button component={RouterLink} to="/login" variant="contained" sx={{ textTransform: 'none' }}>
                  Back to sign in
                </Button>
              </>
            ) : requested ? (
              <>
                <Typography variant="h4" component="h1" sx={{ mb: 1 }}>
                  Check your email
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  If an account matches what you entered, we’ve sent a link to reset
                  your password. It expires in 1 hour. Don’t see it? Check spam, or{' '}
                  <Link component="button" type="button" onClick={() => setRequested(false)}>
                    try a different email or username
                  </Link>
                  .
                </Typography>
                <Button component={RouterLink} to="/login" variant="outlined" sx={{ textTransform: 'none' }}>
                  Back to sign in
                </Button>
              </>
            ) : (
              <>
                <Typography variant="h4" component="h1" sx={{ mb: 1 }}>
                  Reset your password
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Enter the email or username on your teacher account and we’ll send
                  you a reset link.
                </Typography>
                <Stack component="form" onSubmit={onRequest} spacing={2}>
                  <TextFieldLike
                    label="Email or username"
                    value={identifier}
                    onChange={setIdentifier}
                    autoComplete="username"
                  />
                  {error && <Alert severity="error">{error}</Alert>}
                  <Button
                    type="submit"
                    variant="contained"
                    size="large"
                    disabled={submitting || !identifier.trim()}
                    sx={{ textTransform: 'none' }}
                  >
                    {submitting ? 'Sending…' : 'Send reset link'}
                  </Button>
                </Stack>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 3, textAlign: 'center' }}>
                  Signed up with GitHub?{' '}
                  <Link component={RouterLink} to="/login">
                    Sign in with GitHub
                  </Link>{' '}
                  instead — no password needed.
                </Typography>
              </>
            )
          ) : (
            <>
              <Typography variant="h4" component="h1" sx={{ mb: 1 }}>
                Choose a new password
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Pick something at least 8 characters long. You’ll be signed in once
                it’s saved.
              </Typography>
              <Stack component="form" onSubmit={onConfirm} spacing={2}>
                <TextFieldLike
                  label="New password"
                  type="password"
                  value={newPassword}
                  onChange={setNewPassword}
                  autoComplete="new-password"
                />
                <TextFieldLike
                  label="Confirm new password"
                  type="password"
                  value={confirmPw}
                  onChange={setConfirmPw}
                  autoComplete="new-password"
                />
                {error && <Alert severity="error">{error}</Alert>}
                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  disabled={submitting}
                  sx={{ textTransform: 'none' }}
                >
                  {submitting ? 'Saving…' : 'Set new password'}
                </Button>
              </Stack>
            </>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

// Small wrapper so the two forms stay tidy and consistent.
function TextFieldLike({
  label,
  value,
  onChange,
  type = 'text',
  autoComplete,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
  autoComplete?: string;
}) {
  return (
    <TextField
      label={label}
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      autoComplete={autoComplete}
      required
      fullWidth
    />
  );
}

export default ResetPassword;
